import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Swords, Target, Crosshair, ArrowRight, Ban, ShieldCheck, Scale, BadgeCheck, Mail, Globe2, Users, ZoomIn, Shield, Landmark, Trophy, Handshake, Coins } from "lucide-react";
import { BRAND_ACRONYM, BRAND_NAME, BRAND_DOMAIN, BRAND_LOGO, CONTACT_EMAIL } from "../lib/brand";
import { useLang } from "../lib/i18n";
import { ABOUT_COPY } from "../lib/about-copy";
import FAQ from "../lib/about-faq.json";
import BOARD from "../lib/board.json";
import { ImageLightbox } from "../components/ImageLightbox";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { SITE_ORIGIN } from "../lib/seo";

const WRAP = "mx-auto max-w-6xl px-5 sm:px-8 lg:px-12";
const useCopy = () => ABOUT_COPY[useLang().lang];

const DISCIPLINES = [
  { key: "arenabook", Icon: Swords, name: "ArenaBook", to: "/rankings?entity=players&ranking=arenabook" },
  { key: "challenge", Icon: Target, name: "Challenge", to: "/challenge" },
  { key: "strike5", Icon: Crosshair, name: "Strike 5", to: "/strike5" },
];

const STRUCTURE_ICONS = [Users, Shield, Landmark, Globe2];

const LD = {
  "ld-about": () => ({
    "@context": "https://schema.org", "@type": "AboutPage", name: `About ${BRAND_ACRONYM} — ${BRAND_NAME}`,
    url: `${SITE_ORIGIN}/about`, inLanguage: ["en", "el"],
    about: { "@id": `${SITE_ORIGIN}/#organization` }, mainEntity: { "@id": `${SITE_ORIGIN}/#organization` },
  }),
  "ld-faq": () => ({
    "@context": "https://schema.org", "@type": "FAQPage", inLanguage: "en",
    mainEntity: FAQ.en.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  }),
};

// Static about/index.html already ships these; inject only when missing (e.g. SPA navigation).
function useAboutJsonLd() {
  useEffect(() => {
    const added = Object.entries(LD).filter(([id]) => !document.getElementById(id)).map(([id, build]) => {
      const s = document.createElement("script");
      s.type = "application/ld+json"; s.id = id; s.text = JSON.stringify(build());
      document.head.appendChild(s);
      return s;
    });
    return () => added.forEach((s) => s.remove());
  }, []);
}

function Section({ id, n, head: [label, title], children }) {
  return (
    <section id={id} data-testid={`about-section-${id}`} className={`${WRAP} py-14 sm:py-20 border-t border-[rgba(212,175,55,0.12)]`}>
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#E5B842] font-mono font-bold mb-3">{n} · {label}</div>
      <h2 className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-50 leading-none mb-6">{title}</h2>
      {children}
    </section>
  );
}

function GoldLink({ to, children, testid }) {
  return (
    <Link to={to} data-testid={testid} className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[#E5B842] hover:text-[#F7D774]" style={{ transition: "color 0.15s ease" }}>
      {children} <ArrowRight className="w-4 h-4 group-hover:translate-x-1" style={{ transition: "transform 0.2s ease" }} />
    </Link>
  );
}

function Hero() {
  const c = useCopy();
  return (
    <section className="relative overflow-hidden border-b border-[rgba(212,175,55,0.25)]" data-testid="about-hero">
      <img src="/assets/home/hero/about-hero-bg.webp" alt="" aria-hidden="true" className="absolute inset-y-0 right-0 h-full w-full lg:w-[58%] object-cover object-center opacity-45" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, #000 0%, rgba(0,0,0,0.92) 38%, rgba(0,0,0,0.45) 100%)" }} />
      <div className={`${WRAP} relative py-20 sm:py-28`}>
        <img src={BRAND_LOGO} alt={`${BRAND_ACRONYM} logo`} className="h-10 sm:h-12 w-auto mb-8" />
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#E5B842] font-mono font-bold mb-3">{c.heroEyebrow}</div>
        <h1 data-testid="about-title" className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-slate-50 leading-[0.95] max-w-3xl">
          {BRAND_ACRONYM} — <span className="text-[#E5B842]">{BRAND_NAME}</span>
        </h1>
        <p data-testid="about-mission-line" className="text-slate-300 mt-6 text-base sm:text-lg max-w-2xl">
          {c.mission}
        </p>
      </div>
    </section>
  );
}

function WhatIs() {
  const c = useCopy();
  return (
    <Section id="what" n="01" head={c.what}>
      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8">
        <p className="text-slate-300 text-sm sm:text-base leading-relaxed">{c.whatBody}</p>
        <div className="rounded-xl bg-[#0f0c05] border border-[#E5B842]/40 p-5" data-testid="about-no-gambling">
          <div className="flex items-center gap-2 text-[#E5B842] font-bold uppercase tracking-wide text-sm"><Coins className="w-4 h-4" /> {c.vpTitle}</div>
          <p className="text-slate-300 text-sm mt-2 leading-relaxed">{c.vpBody}</p>
        </div>
      </div>
      <div data-testid="about-statement" role="note"
        className="mt-8 rounded-xl border-2 border-[#E5B842] bg-[#120e04] p-6 sm:p-8 flex flex-col sm:flex-row gap-5 sm:items-center shadow-[0_0_40px_rgba(229,184,66,0.12)]">
        <Ban className="w-10 h-10 text-[#E5B842] shrink-0" />
        <p className="font-heading font-black uppercase tracking-tight text-slate-50 text-base sm:text-lg leading-snug">{c.statement}</p>
      </div>
    </Section>
  );
}

function Mission() {
  const c = useCopy();
  const items = [Scale, ShieldCheck, BadgeCheck].map((Icon, i) => ({ Icon, t: c.missionItems[i][0], d: c.missionItems[i][1] }));
  return (
    <Section id="mission" n="02" head={c.missionHead}>
      <div className="grid sm:grid-cols-3 gap-4">
        {items.map(({ Icon, t, d }) => (
          <div key={t} className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.2)] p-5 hover:border-[#E5B842]/50" style={{ transition: "border-color 0.2s ease" }}>
            <Icon className="w-6 h-6 text-[#E5B842]" />
            <div className="font-heading text-xl font-black uppercase text-slate-50 mt-3">{t}</div>
            <p className="text-slate-400 text-sm mt-2 leading-relaxed">{d}</p>
          </div>
        ))}
      </div>
      <div className="mt-6"><GoldLink to="/verify" testid="about-link-verify">{c.verifyBtn}</GoldLink></div>
    </Section>
  );
}

function Disciplines() {
  const c = useCopy();
  return (
    <Section id="disciplines" n="03" head={c.disciplinesHead}>
      <div className="grid md:grid-cols-3 gap-4">
        {DISCIPLINES.map(({ key, Icon, name, to }) => (
          <Link key={key} to={to} data-testid={`about-discipline-${key}`}
            className="group rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.2)] p-6 hover:border-[#E5B842]/60 hover:-translate-y-0.5"
            style={{ transition: "border-color 0.2s ease, transform 0.2s ease" }}>
            <div className="flex items-center justify-between">
              <Icon className="w-7 h-7 text-[#E5B842]" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{c.disciplines[key][0]}</span>
            </div>
            <div className="font-heading text-2xl font-black uppercase text-slate-50 mt-4">{name}</div>
            <p className="text-slate-400 text-sm mt-2 leading-relaxed">{c.disciplines[key][1]}</p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#E5B842]">{c.explore} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1" style={{ transition: "transform 0.2s ease" }} /></div>
          </Link>
        ))}
      </div>
    </Section>
  );
}

function Rankings() {
  const c = useCopy();
  return (
    <Section id="rankings" n="04" head={c.rankingsHead}>
      <p className="text-slate-300 text-sm sm:text-base leading-relaxed max-w-3xl">{c.rankingsBody}</p>
      <div className="flex flex-wrap gap-x-8 gap-y-3 mt-6">
        <GoldLink to="/rankings" testid="about-link-rankings">{c.rankingsLinks[0]}</GoldLink>
        <GoldLink to="/hall-of-fame" testid="about-link-hof">{c.rankingsLinks[1]}</GoldLink>
      </div>
    </Section>
  );
}

function Structure() {
  const c = useCopy();
  return (
    <Section id="structure" n="05" head={c.structureHead}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {c.structure.map(([label, text], i) => { const Icon = STRUCTURE_ICONS[i]; return (
          <div key={i} className="relative rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.2)] p-5">
            <div className="text-[10px] font-mono text-slate-600">0{i + 1}</div>
            <Icon className="w-6 h-6 text-[#E5B842] mt-2" />
            <div className="font-bold uppercase tracking-wide text-slate-50 text-sm mt-3">{label}</div>
            <div className="text-slate-400 text-xs mt-1">{text}</div>
          </div>
        ); })}
      </div>
      <p className="text-slate-400 text-sm mt-5" data-testid="about-club-lifecycle">
        <span className="text-[#E5B842] font-bold uppercase tracking-wide text-xs mr-2">{c.lifecycleLabel}</span>
        {c.lifecycle}
      </p>
      <div className="flex flex-wrap gap-x-8 gap-y-3 mt-5">
        <GoldLink to="/clubs" testid="about-link-clubs">{c.structureLinks[0]}</GoldLink>
        <GoldLink to="/federations" testid="about-link-federations">{c.structureLinks[1]}</GoldLink>
        <GoldLink to="/competitions" testid="about-link-competitions">{c.structureLinks[2]}</GoldLink>
      </div>
    </Section>
  );
}

function Board() {
  const { lang } = useLang();
  const b = BOARD[lang];
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  return (
    <Section id="board" n="06" head={b.head}>
      <p className="text-slate-300 text-sm sm:text-base leading-relaxed max-w-3xl mb-8">{b.intro}</p>
      <button type="button" data-testid="board-image-button" onClick={() => setOpen(true)} aria-label={`${BOARD.alt} — ${b.zoom}`}
        className="group block mx-auto w-full max-w-[1100px] cursor-zoom-in rounded-xl overflow-hidden border border-[rgba(212,175,55,0.25)] hover:border-[#E5B842]/60" style={{ transition: "border-color 0.2s ease" }}>
        <img src={BOARD.src} alt={BOARD.alt} width={1536} height={1024} loading="lazy" data-testid="board-image" className="block w-full h-auto object-contain" />
      </button>
      <div className="mt-3 flex justify-center items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-slate-500"><ZoomIn className="w-3.5 h-3.5 text-[#E5B842]" /> {b.zoom}</div>
      {open && <ImageLightbox src={BOARD.src} alt={BOARD.alt} closeLabel={b.close} onClose={close} />}
    </Section>
  );
}

function Partners() {
  const c = useCopy();
  return (
    <Section id="partners" n="07" head={c.partnersHead}>
      <div className="rounded-xl bg-[#0f0f0f] border border-[rgba(212,175,55,0.2)] p-6 flex flex-col sm:flex-row sm:items-center gap-5">
        <Handshake className="w-8 h-8 text-[#E5B842] shrink-0" />
        <div className="flex-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{c.partnerLabel}</div>
          <div className="font-heading text-2xl font-black uppercase text-slate-50" data-testid="about-partner-assos365">Assos365</div>
          <p className="text-slate-400 text-sm mt-2 leading-relaxed">{c.partnerBody}</p>
        </div>
        <GoldLink to="/rankings?entity=players&scope=partner&partner=assos365" testid="about-link-partner-rankings">{c.partnerBtn}</GoldLink>
      </div>
    </Section>
  );
}

function Faq() {
  const c = useCopy();
  const { lang } = useLang();
  return (
    <Section id="faq" n="09" head={c.faqHead}>
      <Accordion type="multiple" className="rounded-xl border border-[rgba(212,175,55,0.2)] bg-[#0f0f0f] divide-y divide-[rgba(212,175,55,0.12)]" data-testid="about-faq">
        {FAQ[lang].map((f, i) => (
          <AccordionItem key={i} value={`q${i}`} className="border-0 px-5">
            <AccordionTrigger data-testid={`faq-q-${i}`} className="font-sans text-left text-sm sm:text-base font-bold text-slate-100 hover:text-[#E5B842] hover:no-underline [&>svg]:text-[#E5B842]">{f.q}</AccordionTrigger>
            <AccordionContent data-testid={`faq-a-${i}`} className="text-sm text-slate-400 leading-relaxed">
              {f.a}
              {f.link && <div className="mt-3"><GoldLink to={f.link.to} testid={`faq-link-${i}`}>{f.link.label}</GoldLink></div>}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </Section>
  );
}

function Contact() {
  const c = useCopy();
  return (
    <Section id="contact" n="08" head={c.contactHead}>
      <a href={`mailto:${CONTACT_EMAIL}`} data-testid="about-contact-email"
        className="inline-flex items-center gap-3 rounded-xl bg-[#E5B842] text-black px-6 py-4 font-bold hover:brightness-110" style={{ transition: "filter 0.15s ease" }}>
        <Mail className="w-5 h-5" /> {CONTACT_EMAIL}
      </a>
      <p className="text-slate-400 text-sm mt-4">{c.contactBody}</p>
      <div className="mt-12 pt-6 border-t border-[rgba(212,175,55,0.12)] flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-500" data-testid="about-official-domain">
        <Trophy className="w-4 h-4 text-[#E5B842]" /> {c.domainLabel} <span className="text-[#E5B842]">{BRAND_DOMAIN}</span>
      </div>
    </Section>
  );
}

export default function AboutPage() {
  useAboutJsonLd();
  return (
    <div className="animate-fade-up pb-10 bg-[#000000]" data-testid="about-page">
      <Hero />
      <WhatIs />
      <Mission />
      <Disciplines />
      <Rankings />
      <Structure />
      <Board />
      <Partners />
      <Contact />
      <Faq />
    </div>
  );
}
